import { useMemo, useEffect } from 'react'
import { useNavigate } from 'react-router'

import { SurveyErrorState } from '@/components/SurveyErrorState'
import { LoadingScreen } from '@/components/loading-screen'
import { SurveyForm } from '@/components/SurveyForm'
import { useGetSurveys } from '@/hooks/queries/useSurveys'
import { useSubmitAnswers } from '@/hooks/mutations/useSubmitAnswers'
import { Answer, PreviewJD } from '@/interfaces/answer'
import { SurveyType } from '@/interfaces/survey'
import { useLoadingStore } from '@/store/loadingStore'
import { handleError } from '@/utils/helpers'

const CreateJDPage = () => {
  const navigate = useNavigate()
  const { data: survey, isLoading, error } = useGetSurveys(SurveyType.CREATE_JD)
  const { mutate: submitAnswers } = useSubmitAnswers()
  const { showLoading, hideLoading } = useLoadingStore()

  // Get the first active survey's questions
  const questions = useMemo(() => {
    if (!survey || !survey.questions) return []
    return survey.questions.sort((a, b) => a.order - b.order)
  }, [survey])

  // If no active survey, redirect back to home
  useEffect(() => {
    if (!isLoading && !survey) {
      console.error('No active survey found')
      navigate('/')
    }
  }, [isLoading, navigate, survey])

  const handleSubmit = (
    payload: { surveyId: string; answers: Answer[]; targetUserId?: string },
    onSuccess: (answerId: string, previewJD: PreviewJD) => void
  ) => {
    showLoading('Đang tạo Untold JD...')
    submitAnswers(payload, {
      onSuccess: (data) => {
        hideLoading()
        console.log('Survey submitted successfully!', {
          surveyId: payload.surveyId,
          answers: payload.answers,
          targetUserId: payload.targetUserId,
          answerId: data.id,
          previewJD: data.previewJD,
        })
        // Call the onSuccess callback with answerId and previewJD
        onSuccess(data.id, data.previewJD)
      },
      onError: (error) => {
        hideLoading()
        handleError(error, { fallbackMessage: 'Đã xảy ra lỗi khi tạo Untold JD' })
      },
    })
  }

  // Loading state
  if (isLoading) {
    return <LoadingScreen message="Đang tải câu hỏi..." />
  }

  // Error state
  if (error) {
    return <SurveyErrorState title="Lỗi khi tải câu hỏi" message={error.message} />
  }

  // No questions available
  if (!survey || questions.length === 0) {
    return (
      <SurveyErrorState
        title="Không có câu hỏi nào"
        message="Hiện tại không có câu hỏi nào. Vui lòng thử lại sau."
      />
    )
  }

  return (
    <SurveyForm
      title='Có những việc bạn vẫn làm mỗi ngày mà ít người biết.\nHãy kể ra ở đây để xem "Untold JD" của bạn sẽ như thế nào.\nNếu bất ngờ không nhớ được, 4 câu hỏi dưới đây sẽ giúp bạn.'
      highlightedText='"Untold JD"'
      questions={questions}
      onSubmit={handleSubmit}
      surveyId={survey?.id || ''}
    />
  )
}

export default CreateJDPage
